/**
 * Markup de Quals & Traps — `/cvn-quals.html` (Fase 5g.3).
 *
 * Tabla de apontajes por piloto, renderizada sobre la pantalla del
 * monitor de la ready room. Una fila por piloto verificado con
 * apontajes. Columnas: pases, traps, bolters, wave-offs, media LSO y el
 * reparto de traps por número de cable (1-4).
 *
 * La tabla es ordenable: cada cabecera lleva una flecha y, al pulsarla,
 * reordena los datos (mismo patrón que el logbook de pilotos).
 *
 * Los datos salen de `GET /api/greenie-board` — aquí solo se agregan.
 *
 * NOTA: la columna de cualificación (DCQ/NCQ) NO se incluye todavía. El
 * modelo está fijado en ARQUITECTURA.md §13 (`CarrierQualification`:
 * nivel ordinal `none`/`dcq`/`ncq`); solo faltan los criterios objetivos
 * de asignación que define el escuadrón.
 */

import type { GreenieBoardPilot } from '@51esc/shared';

/** Fila de la tabla — un piloto con sus contadores agregados. */
export interface QualRow {
  callsign: string;
  /** Pases totales (trapeados + incompletos). */
  passes: number;
  /** Pases que acabaron en trap. */
  traps: number;
  /** Intentos con grado `B` (bolter). */
  bolters: number;
  /** Intentos con grado `WO` (wave-off). */
  waveOffs: number;
  /** Media de puntos LSO, o `null` si no hay pases computables. */
  avg: number | null;
  /** Reparto de traps por cable [1,2,3,4] en %. Vacío si no hay traps. */
  wirePct: number[];
}

// ─── Ordenación ────────────────────────────────────────────────────────

export type SortKey =
  | 'callsign'
  | 'passes'
  | 'traps'
  | 'bolters'
  | 'waveOffs'
  | 'avg'
  | 'wire1'
  | 'wire2'
  | 'wire3'
  | 'wire4';

export type SortDir = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  dir: SortDir;
}

interface ColumnDef {
  key: SortKey;
  label: string;
}

/** Columnas en orden visual. */
const COLUMNS: ColumnDef[] = [
  { key: 'callsign', label: 'PILOTO' },
  { key: 'passes', label: 'PASES' },
  { key: 'traps', label: 'TRAPS' },
  { key: 'bolters', label: 'BOLTER' },
  { key: 'waveOffs', label: 'WAVE-OFF' },
  { key: 'avg', label: 'MEDIA' },
  { key: 'wire1', label: 'CABLE 1' },
  { key: 'wire2', label: 'CABLE 2' },
  { key: 'wire3', label: 'CABLE 3' },
  { key: 'wire4', label: 'CABLE 4' },
];

/**
 * Dirección por defecto al activar una columna: el callsign asciende
 * (orden alfabético natural); los numéricos descienden (el usuario
 * espera ver al mejor / al más activo arriba).
 */
export function defaultDirFor(key: SortKey): SortDir {
  return key === 'callsign' ? 'asc' : 'desc';
}

/** Valor comparable de una fila para una clave de orden. */
function sortValue(r: QualRow, key: SortKey): number | string {
  switch (key) {
    case 'callsign':
      return r.callsign;
    case 'passes':
      return r.passes;
    case 'traps':
      return r.traps;
    case 'bolters':
      return r.bolters;
    case 'waveOffs':
      return r.waveOffs;
    case 'avg':
      return r.avg ?? -1;
    case 'wire1':
      return r.wirePct[0] ?? -1;
    case 'wire2':
      return r.wirePct[1] ?? -1;
    case 'wire3':
      return r.wirePct[2] ?? -1;
    case 'wire4':
      return r.wirePct[3] ?? -1;
  }
}

/** Sort estable sobre una copia — no muta el array recibido. */
function sortRows(rows: QualRow[], sort: SortState): QualRow[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  const copy = rows.slice();
  copy.sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * factor;
    }
    return (
      String(va).localeCompare(String(vb), 'es', { sensitivity: 'base' }) *
      factor
    );
  });
  return copy;
}

// ─── Agregación ────────────────────────────────────────────────────────

/** Agrega los pilotos del Greenie Board en filas de la tabla de quals. */
export function computeRows(pilots: GreenieBoardPilot[]): QualRow[] {
  return pilots.map(toRow);
}

function toRow(p: GreenieBoardPilot): QualRow {
  let bolters = 0;
  let waveOffs = 0;
  const wireCount = [0, 0, 0, 0];
  for (const pass of p.passes) {
    for (const a of pass.attempts) {
      if (a.grade === 'B') bolters += 1;
      if (a.grade === 'WO') waveOffs += 1;
    }
    const last = pass.attempts[pass.attempts.length - 1];
    if (pass.trapped && last && last.wire !== null) {
      const w = last.wire;
      if (w >= 1 && w <= 4) {
        wireCount[w - 1] = (wireCount[w - 1] ?? 0) + 1;
      }
    }
  }
  const totalWires = wireCount.reduce((s, n) => s + n, 0);
  const wirePct =
    totalWires > 0 ? wireCount.map((n) => (100 * n) / totalWires) : [];
  return {
    callsign: p.callsign,
    passes: p.totalPasses,
    traps: p.trapCount,
    bolters,
    waveOffs,
    avg: p.avgGradePoints,
    wirePct,
  };
}

// ─── Render ────────────────────────────────────────────────────────────

/** Shell estático: marco de la pantalla + zona de cuerpo. */
export function renderQualsShell(): string {
  return `
    <div class="cq-stage">
      <section class="cq-inner" aria-label="Quals & Traps">
        <header class="cq-head">
          <h1 class="cq-title">QUALS &amp; TRAPS · CVN-51</h1>
          <p class="cq-sub" data-zone="updated">cargando…</p>
        </header>
        <div class="cq-body" data-zone="body"></div>
      </section>
    </div>
  `;
}

/**
 * Tabla completa de quals, ordenada según `sort`. Repintar entera al
 * cambiar el orden es barato (decenas de filas a lo sumo).
 */
export function renderQualsTable(rows: QualRow[], sort: SortState): string {
  const head = `
    <thead>
      <tr>${COLUMNS.map((c) => renderHeaderCell(c, sort)).join('')}</tr>
    </thead>`;
  const body = sortRows(rows, sort).map(renderRow).join('');
  return `<table class="cq-table">${head}<tbody>${body}</tbody></table>`;
}

/**
 * Celda de cabecera: un botón con etiqueta + flecha de orden. Flecha
 * `⇅` si la columna está inactiva, `▲`/`▼` si es la activa.
 */
function renderHeaderCell(col: ColumnDef, sort: SortState): string {
  const isActive = sort.key === col.key;
  const arrow = isActive ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅';
  const ariaSort = isActive
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const thCls = col.key === 'callsign' ? ' cq-col-pilot' : '';
  return `
    <th class="cq-th-cell${thCls}" role="columnheader" aria-sort="${ariaSort}">
      <button
        type="button"
        class="cq-th${isActive ? ' cq-th--active' : ''}"
        data-sort-key="${col.key}"
      >
        <span class="cq-th-label">${col.label}</span>
        <span class="cq-th-arrow" aria-hidden="true">${arrow}</span>
      </button>
    </th>
  `;
}

function renderRow(r: QualRow): string {
  const avg = r.avg !== null ? r.avg.toFixed(2) : '—';
  const wire = (i: number): string =>
    r.wirePct.length > 0 ? `${Math.round(r.wirePct[i] ?? 0)}%` : '—';
  return `
    <tr>
      <td class="cq-col-pilot">${esc(r.callsign)}</td>
      <td>${String(r.passes)}</td>
      <td>${String(r.traps)}</td>
      <td>${String(r.bolters)}</td>
      <td>${String(r.waveOffs)}</td>
      <td class="cq-avg">${avg}</td>
      <td>${wire(0)}</td>
      <td>${wire(1)}</td>
      <td>${wire(2)}</td>
      <td>${wire(3)}</td>
    </tr>
  `;
}

/** Nota centrada para los estados de carga / vacío / error. */
export function renderQualsNote(message: string, isError = false): string {
  return `<p class="cq-note${isError ? ' cq-note--error' : ''}">${esc(message)}</p>`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
